/**
 * G-126 (finance audit WO-I, [119] [145]) — the BUSINESS day: the calendar
 * day in Barcelona, which is the day the server counts money in
 * (`src/common/time/business-day.ts`, `BUSINESS_TIME_ZONE`).
 *
 * Finances and the Analytics staff cost ask the server for whole days and
 * months. Before this file the Finances page ran on three clocks — its Salary
 * window on UTC, its Running costs on the browser's own day, the server on
 * Barcelona — so between 00:00 and 02:00 the sections showed different months,
 * and Analytics cut a Barcelona-midnight instant to its UTC date and asked
 * payroll for the day before.
 *
 * Not the same thing as `utils/dayKey.ts`'s `localDayKey`, which is
 * deliberately the device's own day (orders, shifts and tasks the person is
 * looking at on their screen). For a browser in Barcelona the two agree; for
 * the owner reading Finances from abroad only this one matches the server.
 */
export const BUSINESS_TIME_ZONE = "Europe/Madrid";

export type BusinessDay = { year: number; month: number; day: number; ymd: string };

const formatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: BUSINESS_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** The Barcelona calendar day `instant` falls on. Built from parts, never a locale's format. */
export function businessDayOf(instant: Date): BusinessDay {
  const parts = new Map(
    formatter
      .formatToParts(instant)
      .filter((p) => p.type !== "literal")
      .map((p) => [p.type, Number(p.value)]),
  );
  const year = parts.get("year") ?? instant.getUTCFullYear();
  const month = parts.get("month") ?? instant.getUTCMonth() + 1;
  const day = parts.get("day") ?? instant.getUTCDate();
  return {
    year,
    month,
    day,
    ymd: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
  };
}

/** Today in Barcelona. */
export function businessToday(now: Date = new Date()): BusinessDay {
  return businessDayOf(now);
}

/** "YYYY-MM-DD" of the Barcelona day `instant` falls on — what the payroll query's `from`/`to` take. */
export function businessDayKey(instant: Date): string {
  return businessDayOf(instant).ymd;
}

/**
 * KI-120: "YYYY-MM-DD" of the Barcelona day `days` calendar days before today
 * — counted on the calendar, not in 24-hour steps, so a daylight-saving night
 * in between cannot land it on the wrong day.
 */
export function businessDaysAgo(days: number, now: Date = new Date()): string {
  const { year, month, day } = businessToday(now);
  return new Date(Date.UTC(year, month - 1, day - days)).toISOString().slice(0, 10);
}

/**
 * G-175 (KI-53): the Barcelona calendar month `months` months before this one
 * — what Home's "By shop" card offers, so its list never runs on the browser's
 * own month.
 */
export function businessMonthsAgo(months: number, now: Date = new Date()): { year: number; month: number } {
  const { year, month } = businessToday(now);
  const index = year * 12 + (month - 1) - months;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}
